/* Modulo: Slideshow Classic — full-bleed one-up image slideshow.
   JS owns the index; CSS owns the reveal (zero CLS). Slide or cross-fade,
   paired arrows, dynamic line-pill dots, play/pause, keyboard, pointer-swipe,
   autoplay (wraps; pauses on hover/focus and via a persistent control).
   Lazy <picture> hydration, reduced-motion aware, aria-live announced,
   non-current slides aria-hidden + inert, idempotent init, theme-editor
   reload safe + leak-proof. Vanilla IIFE, no deps. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var REDUCE = !!(mq && mq.matches);
  if (mq && mq.addEventListener) {
    mq.addEventListener('change', function (e) { REDUCE = e.matches; });
  }

  var SWIPE = 50;
  var SLIDE_MS = 880;
  var FADE_MS = 620;
  var QUICK_MS = 220;

  function setSlideState(slide, active) {
    if (active) slide.removeAttribute('aria-hidden');
    else slide.setAttribute('aria-hidden', 'true');
    if ('inert' in slide) slide.inert = !active;   // neutralize off-screen slides (tabindex loop below is the fallback)
    var ctrls = slide.querySelectorAll('a, button');
    for (var i = 0; i < ctrls.length; i++) {
      if (active) ctrls[i].removeAttribute('tabindex');
      else ctrls[i].setAttribute('tabindex', '-1');
    }
  }

  function initSlideshow(root) {
    if (root.dataset.moduloSlcReady === '1') return;   // idempotent
    root.dataset.moduloSlcReady = '1';

    var stage  = root.querySelector('[data-stage]');
    var slides = root.querySelectorAll('[data-slide]');
    var total  = slides.length;
    if (!stage || total < 2) return;   // single slide → static hero, no JS work

    var loop       = root.dataset.loop === '1';
    var wantsAuto  = root.dataset.autoplay === '1';
    var interval   = (parseInt(root.dataset.interval, 10) || 6) * 1000;
    var transition = root.dataset.transition || 'slide';

    var dotsHost   = root.querySelector('[data-dots]');
    var liveRegion = root.querySelector('[data-live]');
    var prevBtns   = root.querySelectorAll('[data-prev]');
    var nextBtns   = root.querySelectorAll('[data-next]');
    var pauseBtn   = root.querySelector('[data-playpause]');

    var current = 0;
    var animating = false;
    var timer = null;
    var clearTimer = null;
    var dots = [];
    var userPaused = false;
    var hovering = false;

    if (wantsAuto && !REDUCE) root.classList.add('is-autoplay');

    for (var s = 0; s < total; s++) {
      if (s !== current) setSlideState(slides[s], false);
    }

    /* ── Lazy <picture> hydration ───────────────────────────────────────
       Non-first slides ship with data-src/-srcset on the <img> and
       data-srcset on the mobile <source>, making no request until promoted.
       Promote current + neighbours so distant slides never fetch. */
    function hydrate(slide) {
      if (!slide) return;
      var sources = slide.querySelectorAll('source[data-srcset]');
      for (var j = 0; j < sources.length; j++) {
        sources[j].srcset = sources[j].dataset.srcset;
        sources[j].removeAttribute('data-srcset');
      }
      var imgs = slide.querySelectorAll('img[data-src]');
      for (var i = 0; i < imgs.length; i++) {
        var im = imgs[i];
        if (im.dataset.srcset) im.srcset = im.dataset.srcset;
        im.src = im.dataset.src;
        im.removeAttribute('data-src');
        im.removeAttribute('data-srcset');
      }
    }
    function hydrateAround(i) {
      hydrate(slides[i]);
      var nx = i + 1, pv = i - 1;
      if (loop) { nx = nx % total; pv = (pv + total) % total; }
      if (nx < total) hydrate(slides[nx]);
      if (pv >= 0) hydrate(slides[pv]);
    }

    function buildDots() {
      if (!dotsHost) return;
      while (dotsHost.firstChild) dotsHost.removeChild(dotsHost.firstChild);
      dots = [];
      for (var i = 0; i < total; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'modulo-slc__dot';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + total);
        if (i === 0) b.setAttribute('aria-current', 'true');
        (function (idx) {
          b.addEventListener('click', function () { stop(); goTo(idx); start(); });
        })(i);
        dotsHost.appendChild(b);
        dots.push(b);
      }
    }

    function announce() {
      if (!liveRegion) return;
      var title = slides[current].getAttribute('data-title') || '';
      liveRegion.textContent = 'Slide ' + (current + 1) + ' of ' + total + (title ? ': ' + title : '');
    }

    function toggle(el, off) { off ? el.setAttribute('disabled', '') : el.removeAttribute('disabled'); }

    /* If we're about to disable the arrow that currently holds focus, move
       focus to the opposite arrow (or the stage) so it isn't lost. WCAG 2.4.3. */
    function rescueFocus(group, fallback) {
      for (var i = 0; i < group.length; i++) {
        if (document.activeElement === group[i]) {
          if (fallback && fallback.length && !fallback[0].disabled) fallback[0].focus();
          else if (stage) stage.focus();
        }
      }
    }

    function syncChrome() {
      for (var i = 0; i < dots.length; i++) {
        if (i === current) dots[i].setAttribute('aria-current', 'true');
        else dots[i].removeAttribute('aria-current');
      }
      if (!loop) {
        if (current >= total - 1) rescueFocus(nextBtns, prevBtns);
        if (current <= 0) rescueFocus(prevBtns, nextBtns);
        for (var p = 0; p < prevBtns.length; p++) toggle(prevBtns[p], current <= 0);
        for (var n = 0; n < nextBtns.length; n++) toggle(nextBtns[n], current >= total - 1);
      }
    }

    function goTo(index) {
      var target = loop
        ? ((index % total) + total) % total
        : Math.max(0, Math.min(total - 1, index));
      if (target === current || animating) return;

      var outgoing = slides[current];
      var incoming = slides[target];

      hydrate(incoming);
      animating = true;

      outgoing.classList.add('is-leaving');
      outgoing.classList.remove('is-current');
      setSlideState(outgoing, false);

      incoming.classList.add('is-current');
      setSlideState(incoming, true);
      current = target;
      syncChrome();
      announce();
      hydrateAround(current);

      var dur = REDUCE ? QUICK_MS : (transition === 'fade' ? FADE_MS : SLIDE_MS);
      clearTimeout(clearTimer);
      clearTimer = setTimeout(function () {
        outgoing.classList.remove('is-leaving');
        animating = false;
      }, dur);
    }

    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }
    function autoNext() {
      if (!root.isConnected || REDUCE) { stop(); return; }   // self-heal detached/leaked timer + mid-session reduce
      goTo(loop ? current + 1 : (current + 1) % total);       // autoplay always advances/wraps
    }

    function bind(nl, fn) {
      for (var i = 0; i < nl.length; i++) {
        nl[i].addEventListener('click', function (e) { e.preventDefault(); stop(); fn(); start(); });
      }
    }
    bind(prevBtns, prev);
    bind(nextBtns, next);

    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')       { e.preventDefault(); stop(); prev(); start(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); stop(); next(); start(); }
    });

    var sx = 0, dx = 0, drag = false, moved = false;
    stage.addEventListener('pointerdown', function (e) {
      /* Real controls keep their own behavior. The whole-slide link is the one
         exception: it covers the entire slide, so bailing on it would leave a
         linked slide unswipeable. */
      var hit = e.target.closest('a, button');
      if (hit && !hit.hasAttribute('data-slide-link')) return;
      if (e.button !== undefined && e.button !== 0) return;
      drag = true; moved = false; sx = e.clientX; dx = 0; stop();
    });
    stage.addEventListener('pointermove', function (e) {
      if (!drag) return;
      dx = e.clientX - sx;
      if (Math.abs(dx) > 6) moved = true;
    });

    /* A swipe that starts and ends on the whole-slide link still fires a click.
       Swallow it so swiping changes slides instead of navigating away. */
    stage.addEventListener('click', function (e) {
      if (moved && e.target.closest('[data-slide-link]')) e.preventDefault();
      moved = false;
    }, true);
    function endDrag() {
      if (!drag) return;
      drag = false;
      if (Math.abs(dx) > SWIPE) { dx < 0 ? next() : prev(); }
      dx = 0; start();
    }
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);
    stage.addEventListener('pointerleave', function () { if (drag) endDrag(); });

    /* ── Autoplay state machine ──────────────────────────────────────────
       Runs only when wanted, motion is allowed, the user hasn't paused it,
       and the pointer isn't hovering. start() is the single gate. */
    function canAuto() {
      return wantsAuto && !REDUCE && !userPaused && !hovering && total >= 2 && root.isConnected;
    }
    function start() { if (!canAuto()) return; stop(); timer = setInterval(autoNext, interval); }
    function stop()  { if (timer) { clearInterval(timer); timer = null; } }

    if (wantsAuto) {
      root.addEventListener('mouseenter', function () { hovering = true; stop(); });
      root.addEventListener('mouseleave', function () { hovering = false; start(); });
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
    }

    if (pauseBtn) {
      pauseBtn.addEventListener('click', function () {
        userPaused = !userPaused;
        root.classList.toggle('is-paused', userPaused);
        pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        pauseBtn.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
        if (userPaused) stop(); else start();
      });
    }

    /* teardown so a theme-editor unload/re-render doesn't leak the timer */
    root._slcStop = function () { stop(); clearTimeout(clearTimer); };

    buildDots();
    syncChrome();
    hydrateAround(current);
    start();
  }

  function initAll(scope) {
    var els = (scope || document).querySelectorAll('[data-modulo-slc]');
    for (var i = 0; i < els.length; i++) initSlideshow(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme editor: re-init on section (re)render, tear down on remove. */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-slc]') : null;
    if (el) initSlideshow(el);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-slc]') : null;
    if (el && typeof el._slcStop === 'function') el._slcStop();
  });
})();
